<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import GlobalConfirmDialog from '@/components/GlobalConfirmDialog.vue'
import LandingPageOverlay from '@/components/LandingPageOverlay.vue'
import MaintenanceModeOverlay from '@/components/MaintenanceModeOverlay.vue'
import NotificationToasts from '@/components/NotificationToasts.vue'
import PersistentRouteView from '@/components/PersistentRouteView.vue'
import RecordingModal from '@/components/recording/RecordingModal.vue'
import SyncProgressBar from '@/components/SyncProgressBar.vue'
import { queueLandingPageImport } from '@/services/landingPageImportService'
import { maintenanceModeService } from '@/services/maintenanceModeService'
import { setToastRef, showError } from '@/services/toastService'
import { useAuthStore } from '@/stores/authStore'
import { useRecordingStore } from '@/stores/recordingStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

const authStore = useAuthStore()
const uiStore = useUiStore()
const workspaceStore = useWorkspaceStore()
const recordingStore = useRecordingStore()
const route = useRoute()
const router = useRouter()
const demoCollectionId = '1756893877262-6v4afjcl'
const hasDismissedLandingPage = ref(false)

watch(
  [
    () => route.params.workspaceId as string | undefined,
    () => workspaceStore.clientId,
    () => workspaceStore.name,
    () => workspaceStore._createEventPushed,
    () => workspaceStore._isLoading,
  ],
  ([workspaceId, clientId, workspaceName, hasWorkspaceData, isWorkspaceLoading]) => {
    const hasRoutedWorkspace = Boolean(workspaceId)
    const isRoutedWorkspaceLoaded =
      hasRoutedWorkspace &&
      clientId === workspaceId &&
      hasWorkspaceData &&
      !isWorkspaceLoading &&
      Boolean(workspaceName?.trim())

    document.title = isRoutedWorkspaceLoaded ? `${workspaceName} ~ routecamp` : 'routecamp'
  },
  { immediate: true },
)

// Create refs for components
const toastRef = ref<InstanceType<typeof NotificationToasts> | null>(null)

// Register components with services after they're mounted
watch(toastRef, () => {
  if (toastRef.value) {
    setToastRef(toastRef.value)
    maintenanceModeService.showPendingUpdateToast()
  }
})

watch(
  () => [route.path, authStore.isAuthenticated] as const,
  async ([path, isAuthenticated]) => {
    if (path === '/' && !isAuthenticated && !hasDismissedLandingPage.value) {
      await router.replace({ name: 'landing-page' })
      return
    }

    if (path === '/home') {
      hasDismissedLandingPage.value = false
      uiStore.showLandingPage()
      return
    }

    uiStore.hideLandingPage()
  },
  { immediate: true },
)

const handleLandingPageClose = async () => {
  hasDismissedLandingPage.value = true
  await router.push({ name: 'pinned' })
}

const handleLandingPageViewDemoCollection = async () => {
  hasDismissedLandingPage.value = true
  await router.push({ name: 'workspace-detail', params: { workspaceId: demoCollectionId } })
}

const handleLandingPageRequestInvite = () => {
  window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'signup' } }))
}

const handleLandingPageDropFiles = (files: FileList) => {
  hasDismissedLandingPage.value = true
  if (!queueLandingPageImport(files)) {
    showError('Sign in to import files into Pinned.')
  }
}

onMounted(() => {
  void recordingStore.initialize()
})
</script>

<template>
  <PersistentRouteView />
  <SyncProgressBar />
  <NotificationToasts ref="toastRef" />
  <GlobalConfirmDialog />
  <RecordingModal />
  <LandingPageOverlay
    :is-open="uiStore.isLandingPageVisible"
    @close="handleLandingPageClose"
    @explore-map="handleLandingPageClose"
    @view-demo-collection="handleLandingPageViewDemoCollection"
    @request-invite="handleLandingPageRequestInvite"
    @drop-files="handleLandingPageDropFiles"
  />
  <MaintenanceModeOverlay />
</template>
